import React, { useEffect, useRef, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import UpdatePrixModal from "./UpdatePrixModal";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Alert, Snackbar } from "@mui/material";
import getCurrentUserId from "../reduxSaga/sagas/requests/currentUserId";
import SellingService from "../services/SellingService";
import PrintQrCodeModalPromo from './../pages/ProductSellBTOC/Table/PrintQrCodeModalPromo';
import * as XLSX from "xlsx";

const DEFAULT_PAGE_SIZE = 10;
// maximum page size accepted by getProductsPricesByWarehousePaged
const EXPORT_PAGE_SIZE = 100;

const GROUP_BY_OPTIONS = [
  { value: "barcode", label: "Par code à barre" },
  { value: "productCode", label: "Par code article" },
];

const dateFormatter = (cell) =>
  cell ? new Date(cell).toLocaleDateString("fr-FR") : "";

const quantityFormatter = (cell) =>
  cell !== null && cell !== undefined ? Number(cell) : "";

// selling price of a row: in productCode mode only when every barcode has the same price
const sellingPriceOf = (row) => {
  if (row.barcodes) {
    return row.minSellingPrice !== null && row.minSellingPrice === row.maxSellingPrice
      ? row.minSellingPrice
      : null;
  }
  return row.initial_selling_price;
};

const sellingPriceLabel = (row) => {
  if (row.barcodes && row.minSellingPrice !== row.maxSellingPrice) {
    return `${row.minSellingPrice ?? 0} – ${row.maxSellingPrice ?? 0}`;
  }
  const price = sellingPriceOf(row);
  return price !== null && price !== undefined ? price : 0;
};

// productCode: when set, only the barcodes of that code article are listed (no switch)
const DataTableProductWarehousePricesPrix = ({ idWarehouse, refrech, setRefrech, devise, nameWarehouse, onTotalChange, productCode }) => {
  const [errorAlertText, setErrorAlertText] = useState();
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [row, setRow] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalSize, setTotalSize] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [groupBy, setGroupBy] = useState("barcode");
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("status") === null) {
      setUserRole(["notConnected"]);
    } else {
      setUserRole(JSON.parse(localStorage.getItem("status")));
    }
  }, []);

  const showError = (text) => {
    setErrorAlertText(text);
    setOpenErrorAlert(true);
  };

  const fetchProducts = (page, size, q, mode = groupBy) => {
    if (!idWarehouse) {
      return;
    }
    setIsLoading(true);
    SellingService.getProductsPricesByWarehousePaged(getCurrentUserId(), idWarehouse, {
      page: page - 1, // API uses 0-based page indexing
      size: size,
      q: q.trim() || undefined,
      groupBy: productCode ? "barcode" : mode,
      productCode: productCode || undefined,
    })
      .then((response) => {
        if (response.data.products) {
          setProducts(response.data.products);
          setTotalSize(response.data.totalItems);
          // the "Produits" counter is the number of barcodes
          if (onTotalChange && !q.trim() && mode === "barcode") {
            onTotalChange(response.data.totalItems);
          }
        } else {
          setProducts([]);
          setTotalSize(0);
        }
      })
      .catch((e) => {
        setProducts([]);
        showError("Erreur : " + (e.response?.data?.error || e));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // reload the current page on first render and after a price update
  useEffect(() => {
    fetchProducts(currentPage, sizePerPage, searchQuery);
  }, [idWarehouse, refrech]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, sizePerPage, value);
    }, 500);
  };

  const onChangeGroupBy = (mode) => {
    if (mode === groupBy) {
      return;
    }
    setGroupBy(mode);
    setProducts([]);
    setCurrentPage(1);
    fetchProducts(1, sizePerPage, searchQuery, mode);
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [updateType, setUpdateType] = useState(""); // "qteMin" or "qteMax"
  const handleEdit = (row, type) => {
    setSelectedProduct(row);
    setUpdateType(type);
    setShowUpdateModal(true);
  };

  function buttonImprimerFormatter(cell, row) {
    return (
      <span>
        <button
          className="btn btn-dark"
          onClick={() => {
            openModal()
            setRow(row)
          }}
        >
          <i className="fas fa-qrcode"></i>
        </button>
      </span>
    );
  }

  const lastPurchaseColumns = [
    {
      dataField: "lastPurchaseDate",
      text: "Dernier achat",
      align: "center",
      headerAlign: "center",
      formatter: dateFormatter,
    },
    {
      dataField: "lastPurchaseQuantity",
      text: "Qté dernier achat",
      align: "center",
      headerAlign: "center",
      formatter: quantityFormatter,
    },
  ];

  const barcodesColumn = {
    dataField: "barcodes",
    text: "Codes à barre",
    align: "center",
    headerAlign: "center",
    formatter: (cell) => (
      <span title={(cell || []).join(", ")}>{(cell || []).length}</span>
    ),
  };

  const columns = [
    {
      dataField: "product.name",
      text: "Libellé",
      headerAlign: "center",
    },
    {
      dataField: "product.description",
      text: "Famille & Marque",
      headerAlign: "center",
    },
    {
      dataField: "product.productCode",
      text: "Code article",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "product.barcode",
      text: "Code à barre",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "qte",
      text: "qte",
      align: "center",
      headerAlign: "center",
    },
    ...lastPurchaseColumns,
    {
      dataField: "initial_selling_price",
      text: "Prix de vente",
      align: "center",
      headerAlign: "center",
      formatter: (cell, row) => (
        <>
          {cell !== null && cell !== undefined ? cell : 0}
        </>
      ),
    },
    {
      dataField: "Imprimer",
      text: "Imprimer",
      formatter: buttonImprimerFormatter,
      align: "center",
      headerAlign: "center",
    },
  ];

  function getMarkupValue(row) {
    const sellingPrice = sellingPriceOf(row);
    if (row.achatPrice !== null && row.achatPrice !== undefined && row.achatPrice !== 0 && sellingPrice !== null && sellingPrice !== undefined) {
      return (sellingPrice / row.achatPrice).toFixed(2);
    }
    return sellingPrice !== null && sellingPrice !== undefined
      ? sellingPrice
      : "Non disponible";
  }

  function markupFormatter(cell, row) {
    return <span>{getMarkupValue(row)}</span>;
  }

  const columnsAdmin = [
    {
      dataField: "product.name",
      text: "Libellé",
      headerAlign: "center",
    },
    {
      dataField: "product.description",
      text: "Famille & Marque",
      headerAlign: "center",
    },
    {
      dataField: "product.category.productSubFamily.productFamily.name",
      text: "Famille",
      headerAlign: "center",
    },
    {
      dataField: "product.category.productSubFamily.name",
      text: "Sous Famille",
      headerAlign: "center",
    },
    {
      dataField: "product.category.name",
      text: "Catégorie",
      headerAlign: "center",
    },
    {
      dataField: "product.productCode",
      text: "Code article",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "product.barcode",
      text: "Code à barre",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "qte",
      text: "qte",
      align: "center",
      headerAlign: "center",
    },
    {
      dataField: "achatPrice",
      text: "Dernier Prix d'achat",
      align: "center",
      headerAlign: "center",
      formatter: (cell) => {
        return cell === null || cell === undefined ? "Non disponible" : cell;
      },
    },
    {
      dataField: "markup",
      isDummyField: true,
      text: "Markup",
      align: "center",
      headerAlign: "center",
      formatter: markupFormatter,
    },
    ...lastPurchaseColumns,
    {
      dataField: "initial_selling_price",
      text: "Prix de vente",
      align: "center",
      headerAlign: "center",
      formatter: (cell, row) => (
        <>
          {cell !== null && cell !== undefined ? cell : 0}

          <button
            onClick={() => handleEdit(row, "qteMin")}
            className="btn btn-dark float-right"
          >
            <i className="fas fa-pencil-alt"></i>
          </button>
        </>
      ),
    },
    {
      dataField: "Imprimer",
      text: "Imprimer",
      formatter: buttonImprimerFormatter,
      align: "center",
      headerAlign: "center",
    },
  ];

  // opens the barcodes of the code article in a new tab
  const productCodeColumn = {
    dataField: "productCode",
    text: "Code article",
    align: "center",
    headerAlign: "center",
    formatter: (cell) =>
      cell ? (
        <a
          href={`/warehouseProductCode?idWarehouse=${encodeURIComponent(idWarehouse)}&code=${encodeURIComponent(cell)}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Voir les codes à barre de ce code article"
        >
          {cell}
        </a>
      ) : (
        ""
      ),
  };
  const groupSellingPriceColumn = {
    dataField: "minSellingPrice",
    text: "Prix de vente",
    align: "center",
    headerAlign: "center",
    formatter: (cell, row) => sellingPriceLabel(row),
  };
  // productCode mode: no price edit / QR print, they apply to one barcode
  const columnsByCode = [
    columns[0],
    columns[1],
    productCodeColumn,
    barcodesColumn,
    { ...columns[4], text: "qte (total)" },
    ...lastPurchaseColumns,
    groupSellingPriceColumn,
  ];
  const columnsByCodeAdmin = [
    columnsAdmin[0],
    columnsAdmin[1],
    columnsAdmin[2],
    columnsAdmin[3],
    columnsAdmin[4],
    productCodeColumn,
    barcodesColumn,
    { ...columnsAdmin[7], text: "qte (total)" },
    columnsAdmin[8],
    columnsAdmin[9],
    ...lastPurchaseColumns,
    groupSellingPriceColumn,
  ];

  const handleErrorClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenErrorAlert(false);
  };

  const pageButtonRenderer = ({
    page,
    active,
    disable,
    title,
    onPageChange,
  }) => {
    const handleClick = (e) => {
      e.preventDefault();
      onPageChange(page);
    };
    if (page === "...") {
      return (
        <li className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }
    const activeStyle = active
      ? { backgroundColor: "black", color: "white" }
      : { backgroundColor: "white", color: "black" };
    return (
      <li className="page-item">
        <button
          className="btn btn-dark"
          onClick={handleClick}
          style={activeStyle}
          disabled={disable}
          title={title}
        >
          {page}
        </button>
      </li>
    );
  };

  const options = {
    page: currentPage,
    sizePerPage: sizePerPage,
    totalSize: totalSize,
    pageButtonRenderer,
    showTotal: true,
    paginationSize: 5,
    withFirstAndLast: true,
    alwaysShowAllBtns: false,
    hideSizePerPage: false,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
    ],
  };

  const handleTableChange = (type, { page, sizePerPage: newSize }) => {
    if (type === "pagination") {
      // changing the page size goes back to the first page
      const nextPage = newSize !== sizePerPage ? 1 : page;
      setCurrentPage(nextPage);
      setSizePerPage(newSize);
      fetchProducts(nextPage, newSize, searchQuery);
    }
  };

  // the table only holds one page, so the export reads every page from the API
  const fetchAllProducts = async () => {
    let all = [];
    let page = 0;
    let totalPages = 1;
    while (page < totalPages) {
      const response = await SellingService.getProductsPricesByWarehousePaged(
        getCurrentUserId(),
        idWarehouse,
        {
          page: page,
          size: EXPORT_PAGE_SIZE,
          q: searchQuery.trim() || undefined,
          groupBy: productCode ? "barcode" : groupBy,
          productCode: productCode || undefined,
        }
      );
      if (!response.data.products) {
        throw new Error(response.data.error || "export impossible");
      }
      all = all.concat(response.data.products);
      totalPages = response.data.totalPages;
      page++;
    }
    return all;
  };

  const exportToExcel = async (isAdmin) => {
    setExporting(true);
    try {
      const allProducts = await fetchAllProducts();
      if (allProducts.length === 0) {
        return;
      }

      const byCode = groupBy === "productCode";
      const data = allProducts.map((row) => {
        const sellingPrice = sellingPriceLabel(row);
        const code = byCode ? row.productCode ?? "" : row.product?.productCode ?? "";
        const barcode = byCode ? (row.barcodes || []).join(", ") : row.product?.barcode ?? "";
        const lastPurchase = {
          "Dernier achat": dateFormatter(row.lastPurchaseDate),
          "Qté dernier achat": quantityFormatter(row.lastPurchaseQuantity),
        };

        if (isAdmin) {
          return {
            "Libellé": row.product?.name ?? "",
            "Famille & Marque": row.product?.description ?? "",
            "Famille": row.product?.category?.productSubFamily?.productFamily?.name ?? "",
            "Sous Famille": row.product?.category?.productSubFamily?.name ?? "",
            "Catégorie": row.product?.category?.name ?? "",
            "Code article": code,
            [byCode ? "Codes à barre" : "Code à barre"]: barcode,
            "qte": row.qte ?? "",
            "Dernier Prix d'achat":
              row.achatPrice === null || row.achatPrice === undefined
                ? "Non disponible"
                : row.achatPrice,
            "Markup": getMarkupValue(row),
            ...lastPurchase,
            "Prix de vente": sellingPrice,
          };
        }

        return {
          "Libellé": row.product?.name ?? "",
          "Famille & Marque": row.product?.description ?? "",
          "Code article": code,
          [byCode ? "Codes à barre" : "Code à barre"]: barcode,
          "qte": row.qte ?? "",
          ...lastPurchase,
          "Prix de vente": sellingPrice,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Prix");
      XLSX.writeFile(
        workbook,
        productCode
          ? `produits_prix_code_article_${productCode}_${nameWarehouse || "depot"}.xlsx`
          : `produits_prix_${byCode ? "par_code_article_" : ""}${nameWarehouse || "depot"}.xlsx`
      );
    } catch (e) {
      showError("Erreur : " + (e.message || e));
    } finally {
      setExporting(false);
    }
  };

  const isAdmin = userRole.includes("ADMIN");
  const tableColumns =
    groupBy === "productCode"
      ? isAdmin ? columnsByCodeAdmin : columnsByCode
      : isAdmin ? columnsAdmin : columns;
  const canView = isAdmin || userRole.includes("DIRECTEUR_MAGASIN");

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Prix</h3>
      </div>
      {canView && (
        <div className="card-body">
          <div className="row ml-0 mb-3">
            {!productCode && (
            <div className="btn-group mr-3" role="group">
              {GROUP_BY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`btn ${groupBy === option.value ? "btn-dark" : "btn-outline-dark"}`}
                  // locked during an export so the file always matches the selected view
                  disabled={exporting}
                  onClick={() => onChangeGroupBy(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            )}
            <div>
              <input
                type="text"
                className="form-control custome-search-field"
                placeholder="Chercher (libellé, code, code à barre)"
                value={searchQuery}
                onChange={onSearchChange}
              />
            </div>
            <div className="ml-auto mr-0">
              <button
                className="btn btn-dark"
                disabled={exporting || totalSize === 0}
                onClick={() => exportToExcel(isAdmin)}
              >
                <i className="fas fa-download"></i>{" "}
                {exporting ? "Génération..." : "Télécharger Excel"}
              </button>
            </div>
          </div>
          <div className="row">
            <div className="col-12 px-0">
              <BootstrapTable
                keyField="id"
                data={products}
                columns={tableColumns}
                remote={{ pagination: true, sort: false, filter: false }}
                onTableChange={handleTableChange}
                pagination={paginationFactory(options)}
                striped
                hover
                condensed
                noDataIndication={() => (
                  <div className="text-center py-4">
                    {isLoading ? (
                      <div>
                        <i className="fas fa-spinner fa-spin fa-2x text-dark mb-2"></i>
                        <p>Chargement en cours...</p>
                      </div>
                    ) : (
                      <div>
                        <i className="fas fa-box-open fa-2x text-muted mb-2"></i>
                        <p>Aucun produit trouvé</p>
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      )}

      <PrintQrCodeModalPromo isOpen={showModal} onClose={closeModal} row={row} />
      <Snackbar
        open={openErrorAlert}
        autoHideDuration={3000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleErrorClose} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>
      <UpdatePrixModal
        show={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        product={selectedProduct}
        type={updateType}
        idWarehouse={idWarehouse}
        idUser={getCurrentUserId()}
        refrech={refrech}
        setRefrech={setRefrech}
        devise={devise}
        nameWarehouse={nameWarehouse}
      />
    </div>
  );
};

export default DataTableProductWarehousePricesPrix;
